import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEdit, faArrowUp, faArrowDown, faSync, faEye, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import 'jspdf-autotable';
import baseURL from '../../url';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import useApiWithLoader from '../../../hooks/useApiWithLoader';

export default function NumerosDataOptimized() {
    // Estados principales
    const [numeros, setNumeros] = useState([]);
    const [rifas, setRifas] = useState([]);
    const [counts, setCounts] = useState({
        reservado: 0,
        pendiente: 0,
        disponible: 0,
        total: 0
    });

    // Estados de carga progresiva
    const [loadingStates, setLoadingStates] = useState({
        reservado: false,
        pendiente: false,
        disponible: false,
        counts: false,
        rifas: false
    });

    const [loadedStates, setLoadedStates] = useState({
        reservado: false,
        pendiente: false,
        disponible: false
    });

    // Estados de filtros y UI
    const [modalVisible, setModalVisible] = useState(false);
    const [nuevoNombre, setNuevoNombre] = useState('');
    const [nuevoTelefono, setNuevoTelefono] = useState('');
    const [nuevoEstado, setNuevoEstado] = useState('');
    const [numero, setNumero] = useState({});
    const [identificacion, setIdentificacion] = useState({});
    const [direccion, setDireccion] = useState({});
    const [selectedSection, setSelectedSection] = useState('texto');
    const [filtroId, setFiltroId] = useState('');
    const [filtroEstado, setFiltroEstado] = useState('');
    const [filtroNombre, setFiltroNombre] = useState('');
    const [filtroNumero, setFiltroNumero] = useState('');
    const [filtroNropago, setFiltroNropago] = useState('');
    const [ordenInvertido, setOrdenInvertido] = useState(false);
    const [visibleCount, setVisibleCount] = useState(20);
    const [cantidadTicketsNropago, setCantidadTicketsNropago] = useState(0);

    const { multipleApiCalls } = useApiWithLoader();

    // Función optimizada para cargar conteos
    const loadCounts = useCallback(async () => {
        setLoadingStates(prev => ({ ...prev, counts: true }));
        try {
            const response = await fetch(`${baseURL}/numeroGetCounts.php`, { method: 'GET' });
            const data = await response.json();
            if (data.success) {
                setCounts(data.counts);
            }
        } catch (error) {
            console.error('Error loading counts:', error);
        } finally {
            setLoadingStates(prev => ({ ...prev, counts: false }));
        }
    }, []);

    // Función optimizada para cargar rifas
    const loadRifas = useCallback(async () => {
        setLoadingStates(prev => ({ ...prev, rifas: true }));
        try {
            const response = await fetch(`${baseURL}/rifaGet.php`, { method: 'GET' });
            const data = await response.json();
            if (data.rifas) {
                setRifas(data.rifas);
            }
        } catch (error) {
            console.error('Error loading rifas:', error);
        } finally {
            setLoadingStates(prev => ({ ...prev, rifas: false }));
        }
    }, []);

    // Función optimizada para cargar números por estado (carga todos en lotes)
    const loadNumerosByEstado = useCallback(async (estado, offset = 0, limit = 500) => {
        setLoadingStates(prev => ({ ...prev, [estado]: true }));
        try {
            let allNumeros = [];
            let currentOffset = 0;
            let hasMore = true;
            let totalLoaded = 0;
            
            // Cargar todos los números del estado en lotes
            while (hasMore) {
                const params = new URLSearchParams({
                    estado,
                    limit: limit.toString(),
                    offset: currentOffset.toString()
                });
                
                const response = await fetch(`${baseURL}/numeroGetOptimized.php?${params}`, { method: 'GET' });
                const data = await response.json();
                
                if (data.success && data.numeros && data.numeros.length > 0) {
                    allNumeros = [...allNumeros, ...data.numeros];
                    totalLoaded += data.numeros.length;
                    
                    // Verificar si hay más números para cargar
                    // Usar hasMore de la paginación, o verificar si la cantidad cargada es menor al total
                    const total = data.pagination?.total || 0;
                    hasMore = data.pagination?.hasMore !== false && (total === 0 || totalLoaded < total);
                    
                    // Si no hay más datos en la respuesta, detener
                    if (data.numeros.length < limit) {
                        hasMore = false;
                    }
                    
                    currentOffset += limit;
                } else {
                    hasMore = false;
                }
            }
            
            // Actualizar el estado con todos los números del estado
            setNumeros(prev => {
                const freshIds = new Set(allNumeros.map(n => n.idNumero));
                const withoutEstado = prev.filter(n => n.estado !== estado);
                const withoutDuplicates = withoutEstado.filter(n => !freshIds.has(n.idNumero));
                return [...withoutDuplicates, ...allNumeros];
            });
            
            setLoadedStates(prev => ({ ...prev, [estado]: true }));
        } catch (error) {
            console.error(`Error loading ${estado} numbers:`, error);
        } finally {
            setLoadingStates(prev => ({ ...prev, [estado]: false }));
        }
    }, []);

    // Carga inicial progresiva
    useEffect(() => {
        const loadInitialData = async () => {
            // Cargar conteos y rifas primero (más rápido)
            await Promise.all([
                loadCounts(),
                loadRifas()
            ]);

            // Luego cargar números por orden de prioridad
            // 1. Pendientes (más importante)
            await loadNumerosByEstado('pendiente');
            
            // 2. Reservados
            await loadNumerosByEstado('reservado');
            
            // 3. Disponibles (menos crítico)
            await loadNumerosByEstado('disponible');
        };

        loadInitialData();
    }, [loadCounts, loadRifas, loadNumerosByEstado]);

    const handleShowMore = () => {
        setVisibleCount(prevCount => prevCount + 20);
    };

    const cargarCantidadTicketsNropago = (nropago) => {
        if (!nropago) {
            setCantidadTicketsNropago(0);
            return;
        }
        
        fetch(`${baseURL}/cantidadTicketsNropago.php?nropago=${nropago}`, {
            method: 'GET',
        })
            .then(response => response.json())
            .then(data => {
                if (data.success) {
                    setCantidadTicketsNropago(data.cantidad || 0);
                } else {
                    setCantidadTicketsNropago(0);
                }
            })
            .catch(error => {
                console.error('Error al cargar cantidad de tickets:', error);
                setCantidadTicketsNropago(0);
            });
    };

    const eliminar = (idNumero) => {
        Swal.fire({
            title: '¿Estás seguro?',
            text: '¡No podrás revertir esto!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
        }).then((result) => {
            if (result.isConfirmed) {
                fetch(`${baseURL}/numeroDelete.php?idNumero=${idNumero}`, {
                    method: 'DELETE',
                })
                    .then(response => response.json())
                    .then(data => {
                        Swal.fire(
                            '¡Eliminado!',
                            data.mensaje,
                            'success'
                        );
                        // Recargar solo el estado del número eliminado
                        const numeroEliminado = numeros.find(n => n.idNumero === idNumero);
                        if (numeroEliminado) {
                            loadNumerosByEstado(numeroEliminado.estado);
                        }
                        loadCounts(); // Actualizar conteos
                    })
                    .catch(error => {
                        toast.error(error);
                    });
            }
        });
    };

    const abrirModal = (item) => {
        setNumero(item);
        setNuevoNombre(item.nombre);
        setNuevoTelefono(item.telefono);
        setNuevoEstado(item.estado);
        setDireccion(item.direccion);
        setIdentificacion(item.identificacion);
        setModalVisible(true);
        
        // Cargar cantidad de tickets con el mismo nropago
        cargarCantidadTicketsNropago(item.nropago);
    };

    const cerrarModal = () => {
        setModalVisible(false);
    };

    const handleUpdateText = async (idNumero) => {
        const payload = {
            nombre: nuevoNombre !== undefined ? nuevoNombre : numero.nombre,
            estado: nuevoEstado !== '' ? nuevoEstado : numero.estado,
            telefono: nuevoTelefono !== undefined ? nuevoTelefono : numero.telefono,
            direccion: direccion !== undefined ? direccion : numero.direccion,
            identificacion: identificacion !== undefined ? identificacion : numero.identificacion,
        };

        try {
            const response = await fetch(`${baseURL}/numeroPut.php?idNumero=${idNumero}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });
            const data = await response.json();

            if (data.error) {
                Swal.fire('Error!', data.error, 'error');
                return;
            }

            Swal.fire('Editado!', data.mensaje, 'success');

            const estadoAnterior = numero.estado;
            const estadoFinal = payload.estado;

            setNumeros(prev => prev.map(n => {
                if (n.idNumero === idNumero) {
                    return { ...n, ...payload };
                }

                if (
                    estadoFinal !== estadoAnterior &&
                    numero.nropago &&
                    n.nropago === numero.nropago &&
                    n.idRifa === numero.idRifa
                ) {
                    return { ...n, estado: estadoFinal };
                }

                return n;
            }));

            if (estadoFinal !== estadoAnterior) {
                await loadNumerosByEstado(estadoAnterior);
                await loadNumerosByEstado(estadoFinal);
            } else {
                await loadNumerosByEstado(estadoAnterior);
            }

            loadCounts();
            cerrarModal();
        } catch (error) {
            toast.error(error.message);
        }
    };

    // Filtros optimizados
    const filtrados = numeros.filter(item => {
        const idMatch = !filtroId || item?.idRifa?.toString().includes(filtroId);
        const NombreMatch = !filtroNombre || item?.nombre?.toLowerCase()?.indexOf(filtroNombre.toLowerCase()) !== -1;
        const estadoMatch = !filtroEstado || item?.estado === filtroEstado;
        const numeroMatch = !filtroNumero || item?.numero?.toString().includes(filtroNumero);
        const nropagoMatch = !filtroNropago || item?.nropago?.toString().includes(filtroNropago);
        return idMatch && NombreMatch && estadoMatch && numeroMatch && nropagoMatch;
    });

    const recargar = () => {
        setNumeros([]);
        setLoadedStates({ reservado: false, pendiente: false, disponible: false });
        loadCounts();
        loadRifas();
        loadNumerosByEstado('pendiente');
        loadNumerosByEstado('reservado');
        loadNumerosByEstado('disponible');
    };

    const invertirOrden = () => {
        setNumeros([...numeros].reverse());
        setOrdenInvertido(!ordenInvertido);
    };

    const handleSectionChange = (section) => {
        setSelectedSection(section);
    };

    const descargarExcel = () => {
        const data = filtrados.map(item => ({
            Numero: item.numero,
            Nombre: item.nombre,
            Estado: item.estado,
            Telefono: item.telefono,
            Email: item.email || item.identificacion,
            Referencia: item.referencia || '-',
            'Nro. Pago': item.nropago || '-',
            Direccion: item.direccion,
            Identificacion: item.identificacion,
            Creacion: item.createdAt,
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Numeros');
        XLSX.writeFile(wb, 'numeros.xlsx');
    };

    const descargarPDF = () => {
        const pdf = new jsPDF();
        pdf.text('Lista de numeros', 10, 10);

        const columns = [
            { title: 'Numero', dataKey: 'numero' },
            { title: 'Nombre', dataKey: 'nombre' },
            { title: 'Estado', dataKey: 'estado' },
            { title: 'Telefono', dataKey: 'telefono' },
            { title: 'Email', dataKey: 'email' },
            { title: 'Referencia', dataKey: 'referencia' },
            { title: 'Nro. Pago', dataKey: 'nropago' },
            { title: 'Direccion', dataKey: 'direccion' },
            { title: 'Identificacion', dataKey: 'identificacion' },
            { title: 'Creacion', dataKey: 'createdAt' },
        ];

        const data = filtrados.map(item => ({
            Numero: item.numero,
            Nombre: item.nombre,
            Estado: item.estado,
            Telefono: item.telefono,
            Email: item.email || item.identificacion,
            Referencia: item.referencia || '-',
            nropago: item.nropago || '-',
            Direccion: item.direccion,
            Identificacion: item.identificacion,
            Creacion: item.createdAt,
        }));

        pdf.autoTable({
            head: [columns.map(col => col.title)],
            body: data.map(item => Object.values(item)),
        });

        pdf.save('numeros.pdf');
    };

    // Componente de loader mejorado
    const LoadingSpinner = ({ size = "1x", text = "Cargando..." }) => (
        <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            padding: '20px',
            color: '#666'
        }}>
            <FontAwesomeIcon icon={faSpinner} spin size={size} style={{ marginRight: '10px' }} />
            {text}
        </div>
    );

    // Indicador de progreso de carga
    const LoadingProgress = () => {
        const totalStates = Object.keys(loadedStates).length;
        const loadedCount = Object.values(loadedStates).filter(Boolean).length;
        const progress = (loadedCount / totalStates) * 100;

        return (
            <div style={{ 
                marginBottom: '20px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
            }}>
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '10px'
                }}>
                    <span style={{ fontWeight: 'bold', color: '#495057' }}>
                        Cargando números... ({loadedCount}/{totalStates} estados)
                    </span>
                    <span style={{ fontSize: '14px', color: '#6c757d' }}>
                        {Math.round(progress)}%
                    </span>
                </div>
                <div style={{ 
                    width: '100%', 
                    backgroundColor: '#e9ecef', 
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}>
                    <div style={{ 
                        width: `${progress}%`, 
                        height: '8px', 
                        backgroundColor: '#007bff',
                        transition: 'width 0.3s ease'
                    }}></div>
                </div>
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#6c757d'
                }}>
                    <span>Pendientes: {loadedStates.pendiente ? '✅' : loadingStates.pendiente ? '⏳' : '⏸️'}</span>
                    <span>Reservados: {loadedStates.reservado ? '✅' : loadingStates.reservado ? '⏳' : '⏸️'}</span>
                    <span>Disponibles: {loadedStates.disponible ? '✅' : loadingStates.disponible ? '⏳' : '⏸️'}</span>
                </div>
            </div>
        );
    };

    return (
        <div>
            <ToastContainer />

            <div className='deFlexContent'>
                <div className='deFlex2'>
                    <button className='excel' onClick={descargarExcel}><FontAwesomeIcon icon={faArrowDown} /> Excel</button>
                    <button className='pdf' onClick={descargarPDF}><FontAwesomeIcon icon={faArrowDown} /> PDF</button>
                </div>
                <div className='filtrosContain'>
                    <div className='inputsColumn'>
                        <button className='' onClick={recargar}>
                            {String(filtrados?.length)?.replace(/\B(?=(\d{3})+(?!\d))/g, ".")} / {String(counts.total)?.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                            {loadingStates.counts && <FontAwesomeIcon icon={faSpinner} spin style={{ marginLeft: '5px' }} />}
                        </button>
                    </div>
                    <div className='inputsColumn'>
                        <select value={filtroId} onChange={(e) => setFiltroId(e.target.value)}>
                            <option value="">Rifas</option>
                            {loadingStates.rifas ? (
                                <option disabled>Cargando rifas...</option>
                            ) : (
                                rifas?.map(item => (
                                    <option key={item.idRifa} value={item?.idRifa}>{item?.nombre}</option>
                                ))
                            )}
                        </select>
                    </div>
                    <div className='inputsColumn'>
                        <input type="number" value={filtroNumero} onChange={(e) => setFiltroNumero(e.target.value)} placeholder='Numero' />
                    </div>
                    <div className='inputsColumn'>
                        <input type="text" value={filtroNombre} onChange={(e) => setFiltroNombre(e.target.value)} placeholder='Nombre' />
                    </div>
                    <div className='inputsColumn'>
                        <input type="text" value={filtroNropago} onChange={(e) => setFiltroNropago(e.target.value)} placeholder='Nro. Pago' />
                    </div>
                    <div className='inputsColumn'>
                        <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                            <option value="">Estado</option>
                            <option value='pendiente'>Pendiente ({counts.pendiente})</option>
                            <option value='reservado'>Reservado ({counts.reservado})</option>
                            <option value='disponible'>Disponible ({counts.disponible})</option>
                        </select>
                    </div>
                    <button className='reload' onClick={recargar}>
                        <FontAwesomeIcon icon={faSync} />
                    </button>
                    <button className='reverse' onClick={invertirOrden}>
                        {ordenInvertido ? <FontAwesomeIcon icon={faArrowUp} /> : <FontAwesomeIcon icon={faArrowDown} />}
                    </button>
                </div>
            </div>

            {/* Indicador de progreso de carga */}
            {(loadingStates.reservado || loadingStates.pendiente || loadingStates.disponible) && (
                <LoadingProgress />
            )}

            {modalVisible && (
                <div className="modal">
                    <div className="modal-content">
                        <div className='deFlexBtnsModal'>
                            <div className='deFlexBtnsModal'>
                                <button
                                    className={selectedSection === 'texto' ? 'selected' : ''}
                                    onClick={() => handleSectionChange('texto')}
                                >
                                    Editar Texto
                                </button>
                            </div>
                            <span className="close" onClick={cerrarModal}>
                                &times;
                            </span>
                        </div>
                        <div className='sectiontext' style={{ display: selectedSection === 'texto' ? 'flex' : 'none' }}>
                            <div className='flexGrap'>
                                <fieldset>
                                    <legend>Numero</legend>
                                    <input
                                        type="text"
                                        disabled
                                        value={numero?.numero}
                                    />
                                </fieldset>
                                <fieldset>
                                    <legend>Nombre</legend>
                                    <input
                                        type="text"
                                        value={nuevoNombre}
                                        onChange={(e) => setNuevoNombre(e.target.value)}
                                    />
                                </fieldset>
                                <fieldset>
                                    <legend>Estado</legend>
                                    <select
                                        value={nuevoEstado !== '' ? nuevoEstado : numero.estado}
                                        onChange={(e) => setNuevoEstado(e.target.value)}
                                    >
                                        <option value={numero?.estado}>{numero?.estado}</option>
                                        <option value='disponible'>Disponible</option>
                                        <option value='reservado'>Reservado</option>
                                        <option value='pendiente'>Pendiente</option>
                                    </select>
                                </fieldset>
                                <fieldset >
                                    <legend>Telefono</legend>
                                    <input
                                        type="text"
                                        value={nuevoTelefono}
                                        onChange={(e) => setNuevoTelefono(e.target.value)}
                                    />
                                </fieldset>
                                <fieldset >
                                    <legend>Email</legend>
                                    <input
                                        type="email"
                                        value={numero.email || ''}
                                        disabled
                                        style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                </fieldset>
                                <fieldset >
                                    <legend>Identificacion</legend>
                                    <input
                                        type="text"
                                        value={identificacion}
                                        onChange={(e) => setIdentificacion(e.target.value)}
                                    />
                                </fieldset>
                                <fieldset >
                                    <legend>Direccion</legend>
                                    <input
                                        type="text"
                                        value={direccion}
                                        onChange={(e) => setDireccion(e.target.value)}
                                    />
                                </fieldset>
                                
                                {numero?.nropago && (
                                    <fieldset style={{ 
                                        backgroundColor: '#e8f4fd', 
                                        border: '2px solid #2196F3',
                                        borderRadius: '8px',
                                        padding: '15px',
                                        margin: '10px 0'
                                    }}>
                                        <legend style={{ 
                                            color: '#1976D2', 
                                            fontWeight: 'bold',
                                            fontSize: '14px'
                                        }}>
                                            📊 Información de Pago
                                        </legend>
                                        <div style={{ 
                                            display: 'flex', 
                                            flexDirection: 'column', 
                                            gap: '10px',
                                            alignItems: 'center'
                                        }}>
                                            <div style={{
                                                fontFamily: 'monospace',
                                                fontSize: '16px',
                                                fontWeight: 'bold',
                                                color: '#1976D2',
                                                backgroundColor: '#f5f5f5',
                                                padding: '8px 12px',
                                                borderRadius: '4px',
                                                border: '1px solid #ddd'
                                            }}>
                                                Nro. Pago: {numero.nropago}
                                            </div>
                                            <div style={{
                                                fontSize: '18px',
                                                fontWeight: 'bold',
                                                color: '#2E7D32',
                                                backgroundColor: '#E8F5E8',
                                                padding: '10px 15px',
                                                borderRadius: '6px',
                                                border: '2px solid #4CAF50',
                                                textAlign: 'center',
                                                minWidth: '200px'
                                            }}>
                                                🎫 Cantidad de Tickets: {cantidadTicketsNropago} Tickets
                                            </div>
                                        </div>
                                    </fieldset>
                                )}

                            </div>
                            <button className='btnPost' onClick={() => handleUpdateText(numero.idNumero)}>Guardar</button>
                        </div>
                    </div>
                </div>
            )}

            <div className='table-container'>
                {numeros.length === 0 && !loadingStates.reservado && !loadingStates.pendiente && !loadingStates.disponible ? (
                    <LoadingSpinner text="Cargando números..." />
                ) : (
                    <table className='table'>
                        <thead>
                            <tr>
                                <th>Rifa</th>
                                <th>Estado</th>
                                <th>Numero</th>
                                <th>Nombre</th>
                                <th>Telefono</th>
                                <th>Email</th>
                                <th>Referencia</th>
                                <th>Nro. Pago</th>
                                <th>Identificacion</th>
                                <th>Direccion</th>
                                <th>Imagen</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtrados?.slice(0, visibleCount)?.map(item => (
                                <tr key={item.idNumero}>
                                    {rifas
                                        .filter(rifaFilter => rifaFilter.idRifa === item.idRifa)
                                        .map(rifaFiltrada => (
                                            <td key={rifaFiltrada.idRifa}>{rifaFiltrada?.nombre}</td>
                                        ))
                                    }
                                    <td style={{
                                        color: item?.estado === 'disponible' ? '#008000' : item?.estado === 'reservado' ? '#FF0000' : '#DAA520',
                                    }}>
                                        {`${item?.estado}`}
                                    </td>
                                    <td>{item.numero}</td>
                                    <td>{item?.nombre}</td>
                                    <td>{item.telefono}</td>
                                    <td>{item.email || item.identificacion}</td>
                                    <td>{item.referencia || '-'}</td>
                                    <td style={{ 
                                        fontFamily: 'monospace', 
                                        fontWeight: 'bold', 
                                        color: item.nropago ? '#2c5aa0' : '#999',
                                        fontSize: '12px'
                                    }}>
                                        {item.nropago || '-'}
                                    </td>
                                    <td>{item.identificacion}</td>
                                    <td>{item.direccion}</td>
                                    <td>
                                        {item.imagen_comprobante ? (
                                            <img 
                                                src={`data:image/jpeg;base64,${item.imagen_comprobante}`} 
                                                alt="Comprobante" 
                                                style={{ 
                                                    width: '40px', 
                                                    height: '40px', 
                                                    objectFit: 'cover', 
                                                    borderRadius: '4px',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => {
                                                    // Crear modal para ver imagen en grande
                                                    const modal = document.createElement('div');
                                                    modal.style.cssText = `
                                                        position: fixed; top: 0; left: 0; width: 100%; height: 100%; 
                                                        background: rgba(0,0,0,0.8); z-index: 9999; display: flex; 
                                                        align-items: center; justify-content: center; cursor: pointer;
                                                    `;
                                                    modal.innerHTML = `
                                                        <img src="data:image/jpeg;base64,${item.imagen_comprobante}" 
                                                             style="max-width: 90%; max-height: 90%; object-fit: contain;" 
                                                             alt="Comprobante completo" />
                                                    `;
                                                    modal.onclick = () => document.body.removeChild(modal);
                                                    document.body.appendChild(modal);
                                                }}
                                                title="Hacer clic para ver imagen completa"
                                            />
                                        ) : (
                                            <span style={{ color: '#999', fontSize: '12px' }}>Sin imagen</span>
                                        )}
                                    </td>
                                    <td>
                                        <button className='editar' onClick={() => abrirModal(item)}>
                                            <FontAwesomeIcon icon={faEdit} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            {filtrados?.length > visibleCount && (
                <button onClick={handleShowMore} id="show-more-btn">
                    Mostrar más
                </button>
            )}
        </div>
    );
}

